import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Users } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { useValorDiferido } from '@/shared/ui/useValorDiferido';
import { enDinero } from '@/shared/ui/graficos';
import { BotonesDeExportacion } from './BotonesDeExportacion';
import { CONCEPTOS } from './PanelDeResumen';

interface Deudor {
  identificacion: string;
  nombre: string;
  parroquia: string;
  facturas: number;
  meses: number;
  desde: string;
  hasta: string;
  vencido: number;
  total: number;
}

interface Hoja {
  contenido: Deudor[];
  totalElementos: number;
  pagina: number;
  tamanio: number;
  totalPaginas: number;
  vencido: number;
  total: number;
  parroquias: string[];
}

const mes = (p: string) => (p.length === 7 ? `${p.slice(5)}/${p.slice(0, 4)}` : p);

export function ListaDeDeudores() {
  const [buscar, setBuscar] = useState('');
  const [parroquia, setParroquia] = useState('');
  const [concepto, setConcepto] = useState('');
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const buscado = useValorDiferido(buscar.trim());

  const filtro = {
    ...(buscado ? { buscar: buscado } : {}),
    ...(parroquia ? { parroquia } : {}),
    ...(concepto ? { concepto } : {}),
  };

  const hoja = useQuery({
    queryKey: ['informe-deudores', filtro, pagina, tamanio],
    queryFn: () =>
      api.get<Hoja>('/informes/facturacion/deudores', { query: { ...filtro, pagina, tamanio } }),
  });

  const columnas: Columna<Deudor>[] = [
    {
      clave: 'contribuyente',
      encabezado: 'Contribuyente',
      contenido: (d) => (
        <span className="block">
          <span className="block text-xs font-semibold uppercase text-armazon-800">{d.nombre}</span>
          <span className="importe block text-[10px] text-armazon-400">
            {d.identificacion} · {d.parroquia}
          </span>
        </span>
      ),
    },
    {
      clave: 'facturas',
      encabezado: 'Facturas',
      alinear: 'derecha',
      contenido: (d) => (
        <span className="importe text-xs">{d.facturas.toLocaleString('es-EC')}</span>
      ),
    },
    {
      clave: 'meses',
      encabezado: 'Meses',
      alinear: 'derecha',
      contenido: (d) => (
        <span className="block text-right">
          <span className="importe block text-xs">{d.meses}</span>
          <span className="importe block text-[10px] text-armazon-400">
            {mes(d.desde)} a {mes(d.hasta)}
          </span>
        </span>
      ),
    },
    {
      clave: 'vencido',
      encabezado: 'Vencido',
      alinear: 'derecha',
      contenido: (d) => (
        <span className="importe text-xs text-amber-700">{enDinero(d.vencido)}</span>
      ),
    },
    {
      clave: 'total',
      encabezado: 'Total',
      alinear: 'derecha',
      contenido: (d) => <span className="importe text-xs font-bold">{enDinero(d.total)}</span>,
    },
  ];

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[11px] uppercase text-armazon-500">
          {hoja.data
            ? `${hoja.data.totalElementos.toLocaleString('es-EC')} contribuyentes deben ${enDinero(hoja.data.total)}, ${enDinero(hoja.data.vencido)} ya vencido`
            : 'Contando…'}
        </p>
        <BotonesDeExportacion
          rotulo="Pendientes"
          ruta="/informes/facturacion/deudores/descarga"
          nombre="pendientes-de-cobro"
          parametros={filtro}
        />
      </div>
      <Tabla
        datos={hoja.data}
        cargando={hoja.isPending}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(t) => {
          setTamanio(t);
          setPagina(0);
        }}
        entidad="contribuyentes"
        iconoVacio={Users}
        mensajeVacio="Nadie debe con ese filtro"
        herramientas={
          <span className="flex flex-wrap items-center gap-2">
            <label className="relative">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-armazon-400"
                aria-hidden
              />
              <input
                value={buscar}
                onChange={(e) => {
                  setBuscar(e.target.value);
                  setPagina(0);
                }}
                placeholder="Nombre o cédula…"
                className="h-9 w-52 rounded-lg border border-armazon-200 pl-8 pr-2 text-xs"
              />
            </label>
            <select
              value={parroquia}
              onChange={(e) => {
                setParroquia(e.target.value);
                setPagina(0);
              }}
              aria-label="Parroquia"
              className="h-9 rounded-lg border border-armazon-200 bg-white px-2 text-xs uppercase"
            >
              <option value="">Todas las parroquias</option>
              {(hoja.data?.parroquias ?? []).map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
              <option value="SIN SECTOR">Sin sector</option>
            </select>
            <select
              value={concepto}
              onChange={(e) => {
                setConcepto(e.target.value);
                setPagina(0);
              }}
              aria-label="Concepto"
              className="h-9 rounded-lg border border-armazon-200 bg-white px-2 text-xs uppercase"
            >
              {CONCEPTOS.map((c) => (
                <option key={c.valor} value={c.valor}>
                  {c.etiqueta}
                </option>
              ))}
            </select>
          </span>
        }
      />
    </div>
  );
}
